import {z} from 'zod';
import {LocaleType} from '../../locale/type';
import {requestContext} from '../requestContext';
import {ZOD_FIELD_METADATA_SYMBOL} from './constant';
import type {FieldMetadata, FormField, UiType} from './type';

export function buildMetadata(metadata: FieldMetadata) {
  const refinementFunction = () => {};

  refinementFunction[ZOD_FIELD_METADATA_SYMBOL] = metadata;

  return refinementFunction;
}

export function getMetadataInZodStack(
  schema: z.ZodTypeAny
): FieldMetadata | undefined {
  if (schema._def.typeName === 'ZodEffects') {
    const effect = schema._def.effect;
    const refinementFunction = effect.refinement;

    if (ZOD_FIELD_METADATA_SYMBOL in refinementFunction) {
      return refinementFunction[ZOD_FIELD_METADATA_SYMBOL];
    }
  }

  return undefined;
}

export function getDefaultValueInZodStack(schema: z.ZodTypeAny) {
  if (schema instanceof z.ZodDefault) {
    return schema._def.defaultValue();
  }

  if (schema instanceof z.ZodEffects) {
    return getDefaultValueInZodStack(schema.innerType());
  }

  return undefined;
}

export function getDefaultUiType(schema: z.ZodTypeAny): UiType {
  // TODO(@harry-hi): 추후 zodEffects로 타이핑 수정 필요함. 현재 schema는 zodType이 아니라 zoeEffect라 if문의 instanceof가 의도한대로 동작하지 않는다.
  if (schema instanceof z.ZodString) {
    return 'text';
  }

  if (schema instanceof z.ZodNumber) {
    return 'number';
  }

  if (schema instanceof z.ZodBoolean) {
    return 'checkbox';
  }

  if (schema instanceof z.ZodDate) {
    return 'date';
  }

  return 'text';
}

export function parseField(key: string, schema: z.ZodTypeAny): FormField {
  const fieldMetadata = getMetadataInZodStack(schema);
  const defaultValue = getDefaultValueInZodStack(schema);
  const uiType = fieldMetadata?.uiType ?? getDefaultUiType(schema);

  return {
    key,
    required: !schema.isOptional(),
    default: defaultValue,
    uiType,
    metadata: fieldMetadata,
    schema,
  };
}

export function parseSchemaToFields<T extends z.ZodRawShape>(
  schema: z.ZodObject<T>
) {
  const shape = schema.shape;

  const fields = Object.entries(shape).map(([key, def]) =>
    parseField(key, def)
  );

  return {fields};
}

export function isZodString(schema: z.ZodTypeAny): schema is z.ZodString {
  return schema instanceof z.ZodString;
}

export function getBaseZodType(schema: z.ZodTypeAny): z.ZodTypeAny {
  if (schema instanceof z.ZodDefault || schema instanceof z.ZodOptional) {
    return getBaseZodType(schema._def.innerType);
  }
  if (schema instanceof z.ZodEffects) {
    return getBaseZodType(schema.innerType());
  }
  return schema;
}

export function getFormMetadata(schema: z.ZodTypeAny) {
  const baseSchema = getBaseZodType(schema);

  const {fields} = parseSchemaToFields(
    baseSchema as z.ZodObject<z.ZodRawShape>
  );

  const metadata: Record<string, any> = {};

  fields.forEach(formField => {
    const {key, metadata: fieldMetadata} = formField;
    metadata[key] = fieldMetadata || {};
  });

  return metadata;
}

export const FormFieldSchema: Record<
  LocaleType,
  Record<
    string,
    z.ZodObject<z.ZodRawShape> | z.ZodEffects<z.ZodObject<z.ZodRawShape>>
  >
> = {} as any;

export const setFormFieldSchema = (
  lang: LocaleType,
  schemaName: string,
  schema: z.ZodObject<z.ZodRawShape> | z.ZodEffects<z.ZodObject<z.ZodRawShape>>
) => {
  if (!FormFieldSchema[lang]) {
    FormFieldSchema[lang] = {};
  }
  FormFieldSchema[lang][schemaName] = schema;
};

export const getFormFieldSchema = (
  schemaName: string,
  schema: z.ZodEffects<z.ZodObject<z.ZodRawShape>> | z.ZodObject<z.ZodRawShape>
) => {
  const lang = requestContext.requestInfo?.lang.deviceLang ?? 'ko';

  if (!FormFieldSchema[lang] || !FormFieldSchema[lang][schemaName]) {
    setFormFieldSchema(lang, schemaName, schema);
  }

  const storedSchema = FormFieldSchema[lang][schemaName];
  return storedSchema as z.infer<typeof storedSchema>;
};
